import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { ChevronDown, ChevronUp, FileText } from 'lucide-react';
import ReadinessScoreHeadline from './ReadinessScoreHeadline';
import CategoryRadarChart from './CategoryRadarChart';
import GapsVsStrengths from './GapsVsStrengths';

const categoryLabels = {
  // Polymet prototype mock keys
  operations: 'Operations',
  financial_health: 'Financial Health',
  human_resources: 'Human Resources',
  customer_concentration: 'Customer Concentration',
  growth_strategy: 'Growth Strategy',
  succession_plan: 'Succession Plan',
  // Production CEPA dimension keys (5-category model)
  financial: 'Financial Capital',
  human: 'Human Capital',
  structural: 'Structural Capital',
  customer: 'Customer Capital',
  wealth: 'Wealth Gap',
};

const priorityClasses = {
  high: 'bg-red-500/10 text-red-500 border-red-500/20',
  medium: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
  low: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
};

const AssessmentSnapshotExpertView = ({ snapshot }) => {
  const [questionsExpanded, setQuestionsExpanded] = useState(false);
  const [filterCategory, setFilterCategory] = useState(null);

  const payload = snapshot?.payload || {};
  const categoryScores = payload.category_scores || {};
  const questionResponses = payload.question_responses || [];
  const recommendedActions = payload.recommended_actions || [];

  const overallScore = typeof payload.overall_score === 'number' ? payload.overall_score : 0;
  const band = payload.band || 'developing';

  const actionsByDimension = recommendedActions.reduce((acc, action) => {
    const dim = action.dimension || 'general';
    if (!acc[dim]) acc[dim] = [];
    acc[dim].push(action);
    return acc;
  }, {});

  const categories = Array.from(new Set(questionResponses.map((q) => q.category)));
  const filteredQuestions = filterCategory
    ? questionResponses.filter((q) => q.category === filterCategory)
    : questionResponses;

  const completedAt = payload.completed_at || snapshot?.completed_at;

  return (
    <div className="space-y-8">
      {/* Section A — Headline */}
      <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[16px] p-8">
        <ReadinessScoreHeadline score={overallScore} band={band} />
      </div>

      {/* Section B — Category Radar */}
      {Object.keys(categoryScores).length > 0 && (
        <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[16px] p-8">
          <h2 className="font-mono text-[11px] uppercase text-[hsl(var(--muted-foreground))] tracking-wide mb-6">
            CATEGORY BREAKDOWN
          </h2>
          <CategoryRadarChart categoryScores={categoryScores} />
        </div>
      )}

      {/* Section C & D — Gaps vs Strengths */}
      {Object.keys(categoryScores).length > 0 && (
        <GapsVsStrengths categoryScores={categoryScores} />
      )}

      {/* Section E — Recommended Actions */}
      {recommendedActions.length > 0 ? (
        <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[16px] p-8">
          <div className="flex items-center gap-2 mb-6">
            <FileText className="w-5 h-5 text-[hsl(var(--succeedo-orange))]" />
            <h2 className="text-[18px] font-semibold text-[hsl(var(--foreground))]">
              Recommended Actions
            </h2>
          </div>
          <div className="space-y-6">
            {Object.entries(actionsByDimension).map(([dimension, actions]) => (
              <div key={dimension}>
                <h3 className="text-[14px] font-semibold text-[hsl(var(--foreground))] mb-3">
                  {categoryLabels[dimension] || dimension}
                </h3>
                <div className="space-y-3">
                  {actions.map((action) => (
                    <div
                      key={`${dimension}-${action.action_text}`}
                      className="flex items-start gap-3 p-4 bg-[hsl(var(--muted))]/50 rounded-[8px] border border-[hsl(var(--border))]">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-mono uppercase border ${priorityClasses[action.priority] || priorityClasses.low}`}>
                            {action.priority || 'low'}
                          </span>
                          {action.estimated_effort && (
                            <span className="text-[11px] text-[hsl(var(--muted-foreground))]">
                              Est. {action.estimated_effort}
                            </span>
                          )}
                        </div>
                        <p className="text-[14px] text-[hsl(var(--foreground))]">{action.action_text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[16px] p-8 text-center">
          <FileText className="w-12 h-12 text-[hsl(var(--muted-foreground))]/40 mx-auto mb-4" />
          <h3 className="text-[16px] font-semibold text-[hsl(var(--foreground))] mb-2">
            No Action Plan Yet
          </h3>
          <p className="text-[14px] text-[hsl(var(--muted-foreground))] mb-4">
            Generate a personalized action plan from this assessment to get specific recommendations.
          </p>
        </div>
      )}

      {/* Section F — Question-level Detail */}
      {questionResponses.length > 0 && (
        <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[16px] p-8">
          <button
            type="button"
            onClick={() => setQuestionsExpanded(!questionsExpanded)}
            className="w-full flex items-center justify-between mb-4">
            <h2 className="text-[18px] font-semibold text-[hsl(var(--foreground))]">
              Question-Level Detail
            </h2>
            {questionsExpanded ? (
              <ChevronUp className="w-5 h-5 text-[hsl(var(--muted-foreground))]" />
            ) : (
              <ChevronDown className="w-5 h-5 text-[hsl(var(--muted-foreground))]" />
            )}
          </button>

          {questionsExpanded && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setFilterCategory(null)}
                  className={`px-3 py-1.5 rounded-full text-[12px] font-medium transition-colors ${
                    filterCategory === null
                      ? 'bg-[hsl(var(--succeedo-orange))] text-white'
                      : 'bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]/80'
                  }`}>
                  All Categories
                </button>
                {categories.map((category) => (
                  <button
                    type="button"
                    key={category}
                    onClick={() => setFilterCategory(category)}
                    className={`px-3 py-1.5 rounded-full text-[12px] font-medium transition-colors ${
                      filterCategory === category
                        ? 'bg-[hsl(var(--succeedo-orange))] text-white'
                        : 'bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]/80'
                    }`}>
                    {categoryLabels[category] || category}
                  </button>
                ))}
              </div>

              <div className="space-y-3">
                {filteredQuestions.map((question) => (
                  <div
                    key={question.question_id}
                    className="p-4 bg-[hsl(var(--muted))]/50 rounded-[8px] border border-[hsl(var(--border))]">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <p className="text-[14px] text-[hsl(var(--foreground))] font-medium flex-1">
                        {question.prompt}
                      </p>
                      <span className="text-[11px] font-mono uppercase text-[hsl(var(--muted-foreground))] whitespace-nowrap">
                        {categoryLabels[question.category] || question.category}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-[13px]">
                      <div>
                        <span className="text-[hsl(var(--muted-foreground))]">Answer: </span>
                        <span className="text-[hsl(var(--foreground))] font-medium">
                          {question.answer_label}
                        </span>
                      </div>
                      <div className="text-[hsl(var(--muted-foreground))]">
                        Weight: {question.weight}x
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Metadata Footer */}
      {completedAt && (
        <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[16px] p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-[12px] text-[hsl(var(--muted-foreground))]">
            <div className="space-y-1">
              <div>
                <span className="font-mono uppercase">Completed: </span>
                <span>
                  {new Date(completedAt).toLocaleString(undefined, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
            </div>
            <div className="px-3 py-1.5 bg-[hsl(var(--muted))] rounded-full text-[11px] font-mono uppercase">
              Frozen at last share
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

AssessmentSnapshotExpertView.propTypes = {
  snapshot: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
    preview_metrics: PropTypes.shape({
      primary: PropTypes.string,
      secondary: PropTypes.string,
      tertiary: PropTypes.string,
    }),
    payload: PropTypes.object,
    completed_at: PropTypes.string,
  }).isRequired,
};

export default AssessmentSnapshotExpertView;
